import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "row" }
const _hoisted_2 = { class: "col span-6 policy-field" }
const _hoisted_3 = { class: "sub-description" }
const _hoisted_4 = { class: "row" }
const _hoisted_5 = { class: "col span-6 policy-field" }
const _hoisted_6 = { class: "sub-description" }

import { computed, watch } from 'vue';
import { useStore } from 'vuex';
import { RcSection } from '@components/RcSection';
import { RadioGroup } from '@components/Form/Radio';
import LabeledSelect from '@shell/components/form/LabeledSelect';
import { RcContentGroup } from '@components/Layout';
import { Banner } from '@components/Banner';
import FleetPolicyAllowList from '@shell/components/fleet/FleetPolicyAllowList.vue';
import { useI18n } from '@shell/composables/useI18n';
import type { FleetPolicyNameOption, FleetPolicySource } from '@shell/types/fleet';

type Variant = 'gitRepo' | 'helmOp';

/**
 * GitRepo and HelmOp policies hold the same three settings under different field names,
 * so one section renders both and resolves the field names from the variant.
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'FleetPolicySourceSection',
  props: /*@__PURE__*/_mergeModels({
    value: {},
    variant: {},
    mode: {},
    serviceAccountOptions: { default: () => [] },
    secretOptions: { default: () => [] },
    serviceAccountsRestricted: { type: Boolean, default: false },
    allowedServiceAccounts: { default: () => [] },
    defaultServiceAccountAllowed: { type: Boolean, default: true },
    defaultSecretAllowed: { type: Boolean, default: true }
  }, {
    "restricted": { type: Boolean, ...{ default: false } },
    "restrictedModifiers": {},
  }),
  emits: ["update:restricted"],
  setup(__props: any) {

const SECRET_FIELDS: Record<Variant, { default: keyof FleetPolicySource, allowed: keyof FleetPolicySource }> = {
  gitRepo: { default: 'defaultClientSecretName', allowed: 'allowedClientSecretNames' },
  helmOp:  { default: 'defaultHelmSecretName', allowed: 'allowedHelmSecretNames' },
};

const props = __props;

const { t } = useI18n(useStore());

const secretFields = computed(() => SECRET_FIELDS[props.variant]);
const prefix = computed(() => `fleet.policy.${ props.variant }`);
const testid = computed(() => `fleet-policy-${ props.variant === 'gitRepo' ? 'git-repo' : 'helm-op' }`);

const setField = (field: keyof FleetPolicySource, value: string | string[]) => {
  (props.value as Record<string, unknown>)[field] = value;
};

const allowedSecrets = computed(() => (props.value[secretFields.value.allowed] as string[]) || []);
const restricted = _useModel<boolean>(__props, 'restricted');

const optionName = (option: FleetPolicyNameOption) => (typeof option === 'string' ? option : option.value);

/**
 * A select in the dashboard has no clear button, so a default that has been set is unset by
 * picking None. Its null value never matches the stored name, so the field falls back to its
 * placeholder once it is chosen.
 */
const withNone = (options: FleetPolicyNameOption[]) => [{ label: t('generic.none'), value: null }, ...options];

const defaultServiceAccount = computed({
  get: () => props.value.defaultServiceAccount || '',
  set: (val: string) => {
    props.value.defaultServiceAccount = val || '';
  }
});

// A name the policy would reject is not worth offering, so once the service accounts are
// restricted the default is chosen from the allowed ones. The service accounts are allowed by the
// policy as a whole, while each source holds its own default.
const defaultServiceAccountOptions = computed(() => {
  if (!props.serviceAccountsRestricted) {
    return withNone(props.serviceAccountOptions);
  }

  const options = [...props.allowedServiceAccounts];
  const current = defaultServiceAccount.value;

  // A default that is no longer allowed stays on the list, so the user can see what has to change
  if (current && !options.includes(current)) {
    options.push(current);
  }

  return withNone(options);
});

const defaultSecret = computed({
  get: () => (props.value[secretFields.value.default] as string) || '',
  set: (val: string) => setField(secretFields.value.default, val || ''),
});

// A name the policy would reject is not worth offering, so once the secrets are restricted the
// default is chosen from the allowed ones - including any allowed name that has no secret yet
const optionFor = (name: string) => props.secretOptions.find((option) => optionName(option) === name) || { label: name, value: name };

const defaultSecretOptions = computed(() => {
  if (!restricted.value) {
    return props.secretOptions;
  }

  const options = allowedSecrets.value.map(optionFor);
  const current = defaultSecret.value;

  // A select shows a value it holds no option for as the bare name, losing the type and user the
  // rest of the list carries, so a default that is no longer allowed keeps its own option
  if (current && !options.some((option) => optionName(option) === current)) {
    options.push(optionFor(current));
  }

  return options;
});

const defaultSecretSelectOptions = computed(() => withNone(defaultSecretOptions.value));

const restrictOptions = computed(() => [
  { value: false, label: t(`${ prefix.value }.restrict.all`) },
  {
    value:       true,
    label:       t(`${ prefix.value }.restrict.selected`),
    description: t(`${ prefix.value }.restrict.description`),
  },
]);

// Fleet reads a list left behind as a restriction, so going back to allowing all has to clear it.
// The names are kept for the rest of the session instead of being thrown away, so changing the
// choice back restores them.
let clearedSecrets: string[] = [];

watch(restricted, (val) => {
  if (!val) {
    clearedSecrets = [...allowedSecrets.value];
    setField(secretFields.value.allowed, []);
  } else if (!allowedSecrets.value.length) {
    setField(secretFields.value.allowed, [...clearedSecrets]);
  }
});

const updateAllowed = (val: string[]) => setField(secretFields.value.allowed, val);

// Secrets are offered as options carrying a label, so a name typed in has to be shaped the same
// way, or the select hands back the option object instead of the name the policy stores
const createOption = (name: string) => ({ label: name, value: name });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(RcSection), {
    title: _unref(t)(`${ prefix.value }.title`),
    mode: "with-header",
    type: "primary",
    expandable: "",
    "data-testid": testid.value
  }, {
    default: _withCtx(() => [
      _createVNode(_unref(RcContentGroup), null, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              _createVNode(_unref(LabeledSelect), {
                value: defaultServiceAccount.value,
                "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((defaultServiceAccount).value = $event)),
                options: defaultServiceAccountOptions.value,
                label: _unref(t)(`${ prefix.value }.defaultServiceAccount.label`),
                placeholder: _unref(t)('fleet.policy.placeholder.serviceAccount'),
                mode: props.mode,
                taggable: true,
                searchable: true,
                clearable: true,
                "create-option": createOption,
                "muted-value": !props.defaultServiceAccountAllowed,
                "data-testid": `${ testid.value }-default-service-account`
              }, null, 8, ["value", "options", "label", "placeholder", "mode", "muted-value", "data-testid"]),
              _cache[3] || (_cache[3] = _createTextVNode()),
              _createElementVNode("p", _hoisted_3, _toDisplayString(_unref(t)(`${ prefix.value }.defaultServiceAccount.description`)), 1)
            ])
          ]),
          _cache[4] || (_cache[4] = _createTextVNode()),
          (!props.defaultServiceAccountAllowed)
            ? (_openBlock(), _createBlock(_unref(Banner), {
                key: 0,
                color: "warning",
                label: _unref(t)('fleet.policy.defaultServiceAccountNotAllowed'),
                "data-testid": `${ testid.value }-default-service-account-not-allowed`
              }, null, 8, ["label", "data-testid"]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }),
      _cache[9] || (_cache[9] = _createTextVNode()),
      _createVNode(_unref(RcContentGroup), null, {
        default: _withCtx(() => [
          _createVNode(_unref(RadioGroup), {
            value: restricted.value,
            "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((restricted).value = $event)),
            name: `${ testid.value }-restrict-secrets`,
            options: restrictOptions.value,
            mode: props.mode,
            "aria-label": _unref(t)(`${ prefix.value }.title`),
            "data-testid": `${ testid.value }-restrict-secrets`
          }, null, 8, ["value", "name", "options", "mode", "aria-label", "data-testid"]),
          _cache[6] || (_cache[6] = _createTextVNode()),
          (restricted.value)
            ? (_openBlock(), _createBlock(FleetPolicyAllowList, {
                key: 0,
                value: allowedSecrets.value,
                title: _unref(t)(`${ prefix.value }.allowed.title`),
                label: _unref(t)(`${ prefix.value }.allowed.label`),
                options: props.secretOptions,
                mode: props.mode,
                "data-testid": `${ testid.value }-allowed-secrets`,
                "onUpdate:value": updateAllowed
              }, null, 8, ["value", "title", "label", "options", "mode", "data-testid"]))
            : _createCommentVNode("", true),
          _cache[7] || (_cache[7] = _createTextVNode()),
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _createVNode(_unref(LabeledSelect), {
                value: defaultSecret.value,
                "onUpdate:value": _cache[2] || (_cache[2] = ($event: any) => ((defaultSecret).value = $event)),
                options: defaultSecretSelectOptions.value,
                label: _unref(t)(`${ prefix.value }.defaultSecret.label`),
                placeholder: _unref(t)('fleet.policy.placeholder.secret'),
                mode: props.mode,
                taggable: true,
                searchable: true,
                clearable: true,
                "create-option": createOption,
                "muted-value": !props.defaultSecretAllowed,
                "data-testid": `${ testid.value }-default-secret`
              }, null, 8, ["value", "options", "label", "placeholder", "mode", "muted-value", "data-testid"]),
              _cache[5] || (_cache[5] = _createTextVNode()),
              _createElementVNode("p", _hoisted_6, _toDisplayString(_unref(t)(`${ prefix.value }.defaultSecret.description`)), 1)
            ])
          ]),
          _cache[8] || (_cache[8] = _createTextVNode()),
          (!props.defaultSecretAllowed)
            ? (_openBlock(), _createBlock(_unref(Banner), {
                key: 1,
                color: "warning",
                label: _unref(t)('fleet.policy.defaultSecretNotAllowed'),
                "data-testid": `${ testid.value }-default-secret-not-allowed`
              }, null, 8, ["label", "data-testid"]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["title", "data-testid"]))
}
}

})